import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from 'react';
import pipes from './assets/Pipes.jpg';
import emptyScore from './assets/EmptyScore.png';
import lowGNote from './assets/LowGNote.png';
import './App.css';
import MenuBar from './components/MenuBar';
import Home from './components/Home';
import AnimatedNotes from './components/AnimatedNotes';
import FlashCard from './components/FlashCard';
import Metronome from './components/Metronome';
import Resources from './components/Resources';
import ContactForm from './components/ContactForm';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { initializeStorage } from './utils/storage';
import { playTone as audioPlayTone } from './utils/audioProcessor';
import { useGoogleAnalytics } from './utils/useGoogleAnalytics';
import { ensureUserUUID } from './utils/uuidUtils';
import { clearDialogInstances } from './utils/CommonDialogs';
import { setupAnalytics, trackPageView } from './utils/analytics';
import DirectFunctionTest from './components/DirectFunctionTest';

// Import lighter components directly
import BasicNotes from './components/BasicNotes';
import FeatureRequest from './components/FeatureRequest';
import AboutUs from './components/AboutUs';
import Gracenotes from './components/Gracenotes';
import LoadingComponent from './components/common/LoadingComponent';
import Settings from './components/Settings';
import MicrophoneHelp from './components/MicrophoneHelp';
import LeaderBoard from './components/LeaderBoard';
import Auth from './components/Auth';
import Blocked from './components/Blocked';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';

// Lazy load heavier components
const SheetMusic = lazy(() => import('./components/SheetMusic'));
const Battle = lazy(() => import('./components/Battle'));
const ChanterSync = lazy(() => import('./components/ChanterSync'));
const ChanterTest = lazy(() => import('./components/ChanterTest'));
const Blaster = lazy(() => import('./components/Blaster'));
const ChanterRender = lazy(() => import('./components/ChanterRender'));
const AITest = lazy(() => import('./components/AITest'));

// Lazy load dev-only components
const RenderTest = lazy(() => import('./components/RenderTest').then(module => ({ default: module.RenderTest })));
const FireEffectDemo = lazy(() => import('./components/FireEffectDemo'));
const Template1 = lazy(() => import('./components/Template1'));
const Template2 = lazy(() => import('./components/Template2'));
const DatabaseTest = lazy(() => import('./components/DatabaseTest'));
const AnimateSong = lazy(() => import('./components/AnimateSong'));
const BlasterHighScores = lazy(() => import('./components/BlasterHighScores'));
const PlayAlong = lazy(() => import('./components/PlayAlong'));

// Lazy load Instruction and Profile components
const Instruction = lazy(() => import('./components/Instruction'));
const Profile = lazy(() => import('./components/Profile'));

// Lazy load AISheetNovel component
const AISheetNovel = lazy(() => import('./components/AISheetNovel'));

// Lazy load Tuner component
const Tuner = lazy(() => import('./components/Tuner'));

// Create a wrapper component that uses the Google Analytics hook and handles route changes
const AnalyticsWrapper = ({ children, setActiveSection }) => {
  useGoogleAnalytics();
  const location = useLocation();
  
  // Update active section based on route path
  useEffect(() => {
    // Extract section name from path and update active section
    const path = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;
    if (path === '') {
      setActiveSection('home');
    } else {
      setActiveSection(path);
    }
  }, [location.pathname, setActiveSection]);
  
  // Check for pending redirect
  useEffect(() => {
    // Check for localStorage flag (primary method)
    const forceNavigateFlag = localStorage.getItem('_force_navigate_to_chantersync');
    if (forceNavigateFlag === 'true' && location.pathname !== '/chantersync') {
      console.log('[App Debug] Found localStorage redirect flag, directing to ChanterSync');
      // Clear the flag
      localStorage.removeItem('_force_navigate_to_chantersync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Set active section before navigation
      setActiveSection('chantersync');
      
      // Navigate directly
      window.location.replace(url);
      return;
    }
    
    // Check for sessionStorage flag (legacy/backup method)
    const shouldRedirect = sessionStorage.getItem('_redirect_to_chanter_sync');
    if (shouldRedirect === 'true' && location.pathname !== '/chantersync') {
      console.log('[App Debug] Found sessionStorage redirect flag, directing to ChanterSync');
      sessionStorage.removeItem('_redirect_to_chanter_sync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Navigate directly
      window.location.replace(url);
    }
  }, [location.pathname, setActiveSection]);
  
  // Clear dialog instances when route changes
  useEffect(() => {
    console.log('[App Debug] Route changed to:', location.pathname);
    clearDialogInstances();
  }, [location.pathname]);
  
  return children;
};

// Helper function for safely checking migration notice status
const checkForMigrationNotice = () => {
  try {
    const { shouldShowMigrationNotice } = require('./utils/globalSettings');
    return shouldShowMigrationNotice();
  } catch (error) {
    console.warn('Could not check migration notice status:', error);
    return false;
  }
};

// Helper function for dismissing the notice
const dismissMigrationNoticeHelper = () => {
  try {
    const { dismissMigrationNotice } = require('./utils/globalSettings');
    return dismissMigrationNotice();
  } catch (error) {
    console.warn('Could not dismiss migration notice:', error);
    return false;
  }
};

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isInitialized, setIsInitialized] = useState(false);
  const [imageHeight, setImageHeight] = useState(0);
  const [migrationStatus, setMigrationStatus] = useState({
    needsMigration: false,
    showNotice: false,
    migrated: false
  });
  const [userUUID, setUserUUID] = useState(null);
  const fullscoreRef = useRef(null);
  const metronomeRef = useRef(null);

  // Effect to dynamically set root element widths to match viewport
  useEffect(() => {
    const updateRootWidth = () => {
      const viewportWidth = window.innerWidth;
      document.documentElement.style.width = `${viewportWidth}px`;
      document.body.style.width = `${viewportWidth}px`;
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.width = `${viewportWidth}px`;
      }
      const appElement = document.querySelector('.App');
      if (appElement) {
        appElement.style.width = `${viewportWidth}px`;
      }
    };

    updateRootWidth();
    window.addEventListener('resize', updateRootWidth);
    return () => window.removeEventListener('resize', updateRootWidth);
  }, []);

  // First effect: Check for force navigation flag before anything else
  useEffect(() => {
    const forceNavigateFlag = localStorage.getItem('_force_navigate_to_chantersync');
    if (forceNavigateFlag === 'true') {
      console.log('[App Debug] Found force navigate flag, directing to ChanterSync');
      // Clear the flag to prevent loops
      localStorage.removeItem('_force_navigate_to_chantersync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Set activeSection immediately to prevent incorrect state
      setActiveSection('chanter-sync');
      
      // Navigate directly
      window.location.replace(url);
    }
  }, []);

  // Get active section from localStorage on load
  useEffect(() => {
    const storedActiveSection = localStorage.getItem('active_section');
    if (storedActiveSection) {
      console.log('[App Debug] Setting active section from localStorage:', storedActiveSection);
      setActiveSection(storedActiveSection);
      
      // Clear it after use to avoid issues on other page loads
      localStorage.removeItem('active_section');
    }
  }, []);

  useEffect(() => {
    // Initialize storage and check for migrations
    const initResult = initializeStorage(true); // true = run automatic migrations
    
    console.log('Storage initialization result:', initResult);
    
    // Ensure we have a UUID for the user
    const uuid = ensureUserUUID();
    setUserUUID(uuid);
    console.log('User UUID initialized:', uuid);
    
    // Set initial status
    setMigrationStatus({
      needsMigration: initResult.needsMigration,
      showNotice: false, // Initialize to false, we'll check again after a delay
      migrated: initResult.migrated
    });
    
    setIsInitialized(true);
    
    // Check for migration notice after a short delay to ensure all modules are loaded
    const noticeCheckTimer = setTimeout(() => {
      const showNotice = checkForMigrationNotice();
      setMigrationStatus(prev => ({
        ...prev,
        showNotice
      }));
    }, 500);
    
    return () => clearTimeout(noticeCheckTimer);
  }, []);

  // Handle dismissing the migration notice
  const handleDismissMigrationNotice = () => {
    dismissMigrationNoticeHelper();
    setMigrationStatus(prev => ({
      ...prev,
      showNotice: false
    }));
  };

  useEffect(() => {
    if (fullscoreRef.current) {
      const updateHeight = () => {
        setImageHeight(fullscoreRef.current.clientHeight);
      };
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [activeSection]);

  const playTone = useCallback((frequency, audioContext, options = {}) => {
    return audioPlayTone(frequency, audioContext, options);
  }, []);

  // Array of 9 identical images for the row
  const rowImages = Array(9).fill(lowGNote);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <AnalyticsWrapper setActiveSection={setActiveSection}>
        <div className="App">
          <MenuBar activeSection={activeSection} setActiveSection={setActiveSection} />
          
          {/* Show migration notice if needed */}
          {migrationStatus.showNotice && (
            <div className="migration-notice">
              <div className="migration-notice-content">
                <p>Your settings have been updated. Please verify your microphone and chanter selections.</p>
                <button 
                  className="migration-notice-button"
                  onClick={handleDismissMigrationNotice}
                >
                  Got it
                </button>
                <button 
                  className="migration-notice-button migration-notice-settings-button"
                  onClick={() => {
                    setActiveSection('settings');
                    window.location.href = '/settings';
                    handleDismissMigrationNotice();
                  }}
                >
                  Go to Settings
                </button>
              </div>
            </div>
          )}
          
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/flashcards" element={<FlashCard />} />
              <Route path="/metronome" element={<Metronome ref={metronomeRef} />} />
              <Route path="/basic-notes" element={<BasicNotes playTone={playTone} />} />
              <Route path="/play-along" element={
                <Suspense fallback={<LoadingComponent message="Loading Play Along..." />}>
                  <PlayAlong />
                </Suspense>
              } />
              
              {/* Lazy-loaded routes */}
              <Route path="/chanter-sync" element={
                <Suspense fallback={<LoadingComponent message="Loading Chanter Sync..." />}>
                  <ChanterSync />
                </Suspense>
              } />
              <Route path="/chantersync" element={
                <Suspense fallback={<LoadingComponent message="Loading Chanter Sync..." />}>
                  <ChanterSync />
                </Suspense>
              } />
              <Route path="/sheet-music" element={<Suspense fallback={<LoadingComponent />}><SheetMusic /></Suspense>} />
              <Route path="/chanter-test" element={
                <Suspense fallback={<LoadingComponent message="Loading Chanter Test..." />}>
                  <ChanterTest />
                </Suspense>
              } />
              <Route path="/battle" element={
                <Suspense fallback={<LoadingComponent message="Loading Battle Game..." />}>
                  <Battle />
                </Suspense>
              } />
              <Route path="/blaster" element={
                <Suspense fallback={<LoadingComponent message="Loading Blaster Game..." />}>
                  <Blaster />
                </Suspense>
              } />
              <Route path="/animated-notes" element={
                <Suspense fallback={<LoadingComponent message="Loading Animated Notes..." />}>
                  <AnimatedNotes />
                </Suspense>
              } />
              <Route path="/chanter-render" element={
                <Suspense fallback={<LoadingComponent message="Loading Chanter View..." />}>
                  <ChanterRender />
                </Suspense>
              } />
              <Route path="/gracenotes" element={
                <Suspense fallback={<LoadingComponent message="Loading Gracenotes..." />}>
                  <Gracenotes />
                </Suspense>
              } />
              
              {/* Regular routes */}
              <Route path="/resources" element={<Resources />} />
              <Route path="/contact" element={<ContactForm />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/feature-request" element={<FeatureRequest />} />
              <Route path="/microphone-help" element={<MicrophoneHelp />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/leader-board" element={<LeaderBoard />} />
              
              {/* Dev-only routes */}
              <Route path="/render-test" element={
                <Suspense fallback={<LoadingComponent message="Loading Render Test..." />}>
                  <RenderTest />
                </Suspense>
              } />
              <Route path="/fire-demo" element={
                <Suspense fallback={<LoadingComponent message="Loading Fire Demo..." />}>
                  <FireEffectDemo />
                </Suspense>
              } />
              <Route path="/template1" element={
                <Suspense fallback={<LoadingComponent message="Loading Template 1..." />}>
                  <Template1 />
                </Suspense>
              } />
              <Route path="/template2" element={
                <Suspense fallback={<LoadingComponent message="Loading Template 2..." />}>
                  <Template2 />
                </Suspense>
              } />
              <Route path="/database-test" element={
                <Suspense fallback={<LoadingComponent message="Loading Database Test..." />}>
                  <DatabaseTest />
                </Suspense>
              } />
              <Route path="/tuner" element={
                <Suspense fallback={<LoadingComponent message="Loading Tuner..." />}>
                  <Tuner />
                </Suspense>
              } />
              <Route path="/animate-song" element={
                <Suspense fallback={<LoadingComponent message="Loading Song Animation..." />}>
                  <AnimateSong />
                </Suspense>
              } />
              <Route path="/aitest" element={
                <Suspense fallback={<LoadingComponent message="Loading AI Analysis..." />}>
                  <AITest />
                </Suspense>
              } />
              <Route path="/ai-test" element={
                <Suspense fallback={<LoadingComponent message="Loading AI Analysis..." />}>
                  <AITest />
                </Suspense>
              } />
              <Route path="/blaster-high-scores" element={
                <Suspense fallback={<LoadingComponent message="Loading High Scores..." />}>
                  <BlasterHighScores />
                </Suspense>
              } />
              <Route path="/directfunctiontest" element={<DirectFunctionTest />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/blocked" element={<Blocked />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/profile" element={
                  <Suspense fallback={<LoadingComponent message="Loading Profile..." />}>
                    <Profile />
                  </Suspense>
                } />
              </Route>
              <Route path="/instruction" element={
                <Suspense fallback={<LoadingComponent message="Loading Instruction..." />}>
                  <Instruction />
                </Suspense>
              } />
              <Route path="/aisheetnovel" element={
                <Suspense fallback={<LoadingComponent message="Loading AI Sheet Novel..." />}>
                  <AISheetNovel />
                </Suspense>
              } />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
        </div>
      </AnalyticsWrapper>
    </Router>
  );
}

export default App;
